import { Inject, Injectable } from '@angular/core';
import type Keyboard from 'simple-keyboard';
// DOCUMENT iz @angular/common: re-export iz @angular/core postoji tek od ng20,
// a core mora da se kompajlira i pod ng16.
import { DOCUMENT } from '@angular/common';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class VirtualKeyboardService {
  keyboard?: Keyboard;
  value: string = '';
  changed?: Subject<string>;

  // simple-keyboard se učitava dinamički pri prvom otvaranju tastature — ne ulazi u
  // inicijalni bundle. Javni API (open/close/toggle/changed) ostaje sinhron: `changed`
  // postoji odmah posle open() (pozivaoci se subscribuju u istom tick-u), a setInput
  // se primeni čim se modul učita.
  private loading?: Promise<void>;
  private shouldBeOpen = false;

  constructor(@Inject(DOCUMENT) private document: HTMLDocument) { }

  get isOpen() {
    return this.shouldBeOpen;
  }

  open(initialValue: string = '') {
    this.shouldBeOpen = true;
    this.value = initialValue;

    this.changed?.complete();
    this.changed = new Subject<string>();

    if (this.keyboard) {
      this.keyboard.setInput(initialValue);
      return;
    }

    this.ensureKeyboard().then(() => {
      if (this.shouldBeOpen) {
        this.keyboard?.setInput(this.value);
      }
    });
  }

  close() {
    this.shouldBeOpen = false;
    if (!this.keyboard) {
      return;
    }

    this.keyboard.destroy();
    this.keyboard = undefined;
  }

  toggle(initialValue: string = '') {
    if (this.shouldBeOpen) {
      this.close();
    } else {
      this.open(initialValue);
    }
  }

  private ensureKeyboard(): Promise<void> {
    if (!this.loading) {
      this.loading = import('simple-keyboard')
        .then(m => {
          // close() je mogao da stigne dok se modul učitavao
          if (!this.shouldBeOpen || this.keyboard) {
            return;
          }

          // Create and append keyboard container
          var container = this.document.getElementsByClassName('simple-keyboard')[0];

          if (!container) {
            container = this.document.createElement('div');
            this.document.body.appendChild(container);
            container.className = 'simple-keyboard';
          }

          this.keyboard = new m.default({
            container: container,
            onChange: (input: string) => this.onChange(input),
            onKeyPress: (button: string) => this.onKeyPress(button)
          });
        })
        .finally(() => {
          this.loading = undefined;
        });
    }

    return this.loading;
  }

  onChange = (input: string) => {
    this.value = input;
    this.changed?.next(this.value);
  };

  onKeyPress = (button: string) => {
    /**
     * If you want to handle the shift and caps lock buttons
     */
    if (button === "{shift}" || button === "{lock}") this.handleShift();
  };

  onInputChange = (event: any) => {
    this.keyboard?.setInput(event.target.value);
  };

  handleShift = () => {
    if (!this.keyboard) {
      return;
    }
    let currentLayout = this.keyboard.options.layoutName;
    let shiftToggle = currentLayout === "default" ? "shift" : "default";

    this.keyboard.setOptions({
      layoutName: shiftToggle
    });
  };

}
